import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Camera, Trash2 } from "lucide-react";
import EditableDetailRow from "./EditableDetailRow";
import LogoUploadDialog from "../../../components/LogoUploadDialog";
import { updateItemLogo } from "../../../api/ProjectApi";

interface AvatarUploadRowProps {
  /** The record ID to update */
  itemId: string;
  /** Current avatar/logo URL */
  iconUrl: string;
  /** Display name used for fallback initial letter */
  displayName: string;
  /** Record type passed to LogoUploadDialog for correct placeholder rendering */
  type: "COMPANY" | "CONTACT";
  /** Called after a successful upload or removal so the parent can refresh state */
  onSuccess: () => void;
}

const FallbackAvatar: React.FC<{ name: string; size: "sm" | "md" }> = ({ name, size }) => {
  const sizeClass = size === "sm" ? "w-[26px] h-[26px] text-[10px]" : "w-6 h-6 text-[10px]";
  return (
    <div
      className={`rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 ${sizeClass}`}
    >
      <span className="text-white font-semibold">{name.charAt(0).toUpperCase()}</span>
    </div>
  );
};

const AvatarPreview: React.FC<{ iconUrl: string; name: string; size: "sm" | "md" }> = ({
  iconUrl,
  name,
  size,
}) => {
  const sizeClass = size === "sm" ? "w-[26px] h-[26px]" : "w-6 h-6";
  if (!iconUrl) return <FallbackAvatar name={name} size={size} />;
  return (
    <div className={`rounded-full overflow-hidden flex-shrink-0 ${sizeClass}`}>
      <img
        src={iconUrl}
        alt="avatar"
        className="w-full h-full object-cover"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = `<div class="w-full h-full rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-semibold">${name.charAt(0).toUpperCase()}</div>`;
          }
        }}
      />
    </div>
  );
};

/**
 * Reusable avatar/logo upload row for use inside Account and Contact detail panels.
 * Renders an EditableDetailRow with preview, upload button, and optional delete button.
 */
const AvatarUploadRow: React.FC<AvatarUploadRowProps> = ({
  itemId,
  iconUrl,
  displayName,
  type,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const label = type === "CONTACT" ? t("Avatar") : t("Logo");

  const handleUploadSave = async (base64: string) => {
    await updateItemLogo(itemId, base64);
    setDialogOpen(false);
    onSuccess();
  };

  const handleRemove = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      await updateItemLogo(itemId, "");
      onSuccess();
    } catch (err) {
      console.error("Failed to remove avatar:", err);
    } finally {
      setRemoving(false);
    }
  };

  // --- Edit mode content (shown inside editable popover/inline area) ---
  const editComponent = (
    <div className="flex items-center gap-2">
      <AvatarPreview iconUrl={iconUrl} name={displayName} size="sm" />
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-600 text-white text-[10px] leading-4 font-medium rounded-full hover:bg-blue-700 transition-colors"
      >
        <Camera className="w-2.5 h-2.5" />
        <span>{t("Upload")}</span>
      </button>
      {iconUrl && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={removing}
          className="flex-shrink-0 text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
          title={t("Remove")}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  // --- Read-only (collapsed) view ---
  const readView = iconUrl ? (
    <div className="flex items-center gap-2">
      <AvatarPreview iconUrl={iconUrl} name={displayName} size="md" />
      <span className="text-[12px] leading-4 font-medium text-gray-800 truncate max-w-[200px]">
        {t("Profile Picture")}
      </span>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <FallbackAvatar name={displayName} size="md" />
      <span className="text-gray-500">{t("Set a value...")}</span>
    </div>
  );

  return (
    <>
      <div className="flex items-center w-full">
        <div className="flex items-center">
          <Image className="w-4 h-4 text-gray-500" />
        </div>
        <EditableDetailRow label={label} editComponent={editComponent}>
          {readView}
        </EditableDetailRow>
      </div>

      <LogoUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleUploadSave}
        type={type}
      />
    </>
  );
};

export default AvatarUploadRow;
